import { useState } from "react";
import { useAuth } from "../../../context";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../api/useFetchObjects";
import useAdd from "../../../api/useAdd";
import useUpdate from "../../../api/useUpdate";
import useDelete from "../../../api/useDelete";
import SeminarAttendanceFormDialog from "./SeminarAttendanceFormDialog";
import { Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TablePagination, CircularProgress, Alert, Chip, Button, IconButton } from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";

export default function SeminarAttendancesList() {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState(null);

  const { data: seminarsData } = useFetchObjects(["seminars-sa"], "seminars/?page_size=500", token);
  const { data: academicsData } = useFetchObjects(["academics-sa"], "academics/?page_size=500", token);
  const { data, isLoading, isError, error } = useFetchObjects(
    ["seminar-attendances", page, rowsPerPage],
    `seminar-attendances/?page=${page}&page_size=${rowsPerPage}`,
    token
  );

  const { handleAdd } = useAdd("seminar-attendances", token, t("academic.attendanceAdded", "Attendance added."));
  const { handleUpdate } = useUpdate("seminar-attendances", token);
  const { handleDelete, ConfirmDialog } = useDelete("seminar-attendances", token);

  const rows = Array.isArray(data) ? data : (data?.results || []);
  const totalCount = Array.isArray(data) ? data.length : (data?.count || 0);
  const seminars = seminarsData?.results || seminarsData || [];
  const academics = academicsData?.results || academicsData || [];

  const handleOpenAdd = () => { setEditingAttendance(null); setDialogOpen(true); };
  const handleOpenEdit = (row) => { setEditingAttendance(row); setDialogOpen(true); };
  const handleCloseDialog = () => { setDialogOpen(false); setEditingAttendance(null); };
  const handleSave = (payload) => { if (editingAttendance) handleUpdate(editingAttendance.id, payload); else handleAdd(payload); handleCloseDialog(); };

  if (isError) return <Alert severity="error" sx={{ m: 2 }}>{error?.message || "Failed to load seminar attendances"}</Alert>;
  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <ConfirmDialog />
      <Paper sx={{ p: 3, width: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>{t("academic.seminarAttendances", "Seminar Attendances")}</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>{t("academic.addAttendance", "Add Attendance")}</Button>
        </Box>
        {isLoading ? <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box> : (
          <>
            <TableContainer sx={{ width: "100%", overflow: "auto" }}>
              <Table size="small" sx={{ minWidth: 400 }}>
                <TableHead><TableRow sx={{ bgcolor: "grey.200" }}>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>{t("academic.seminar", "Seminar")}</strong></TableCell>
                  <TableCell><strong>{t("academic.trainee", "Trainee")}</strong></TableCell>
                  <TableCell><strong>{t("academic.attended", "Attended")}</strong></TableCell>
                  <TableCell align="right"><strong>{t("common.actions", "Actions")}</strong></TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {rows.length === 0 ? <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>{t("common.noRecords", "No records found.")}</TableCell></TableRow> : rows.map((row, idx) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{(page - 1) * rowsPerPage + idx + 1}</TableCell>
                      <TableCell>{row.seminar_display || "—"}</TableCell>
                      <TableCell>{row.academic_display || "—"}</TableCell>
                      <TableCell><Chip size="small" label={row.attended ? t("common.yes", "Yes") : t("common.no", "No")} color={row.attended ? "success" : "default"} /></TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenEdit(row)} title={t("common.edit", "Edit")}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => handleDelete(row.id)} color="error" title={t("common.delete", "Delete")}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={totalCount} page={page - 1} onPageChange={(e, p) => setPage(p + 1)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(1); }} rowsPerPageOptions={[10, 25, 50]} />
          </>
        )}
      </Paper>
      <SeminarAttendanceFormDialog open={dialogOpen} onClose={handleCloseDialog} attendance={editingAttendance} seminars={seminars} academics={academics} onSave={handleSave} saving={false} />
    </Box>
  );
}
